/**
 * Service: cart
 * It maintains the state of the shopping cart
 * items - cart items of type event, inventory, membership, donation
 * id - cart id
 * couponCodes - coupons applied to the cart
 * membershipIds - membership ids of the transacting member (can be more than one - base + aux)
 * membershipCategoryId - membership level id of new membership being purchased.
 */

import { currencyFloatToInteger, integerToCurrencyFloat } from '@acme/b2b/pricing';
import { ApiService } from '@b2b/common/services/api/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import {
  AutorenewInCart,
  CartFormItem,
  CartOrderForm,
  createAutorenewInCart,
  GiftCardRedemption,
  ShoppingCartItem,
} from '@b2b/types/cart';
import { TODO } from '@b2b/types/core/utils';
import { CartEventOrderForm } from '@b2b/types/event';
import { CartMembershipOrderForm, isMembershipOrderForm, Membership } from '@b2b/types/membership';
import { OrderContact } from '@b2b/types/order';
import { Organization } from '@b2b/types/organization';
import {
  deduplicateFormsByDisplaySettings,
  filterEventFormsBySalesChannel,
  filterMembershipFormsBySalesChannel,
} from '@b2b/views/application/views/cart/components/cart-forms/utils/form-filters.utils';
import angular, { IPromise, IQService } from 'angular';
import { BehaviorSubject, combineLatest, first, firstValueFrom, map } from 'rxjs';
import { CalendarService } from '../calendar/types';
import { ThemesHelperService } from '../themesHelper/types';
import { UserPreferencesService } from '../userPreferences/types';
import { cartOrderFormToCartFormItem } from './cart.service.utils';
import type { CartCouponsService } from './coupons/coupons.service';
import { CartCouponsServiceModule } from './coupons/coupons.service';
import type { CartEventsService } from './events/events.service';
import { CartEventsServiceModule } from './events/events.service';
import { CartMembershipsService, CartMembershipsServiceModule } from './memberships/memberships.service';

type CartStatus = 'idle' | 'loading';
interface CartState {
  status: CartStatus;
  autorenewInCart: AutorenewInCart;
}

function createInitialState(): CartState {
  return {
    status: 'idle',
    autorenewInCart: createAutorenewInCart(),
  };
}

export class CartService {
  static $inject = [
    '$q',
    'ApiService',
    'HelpersService',
    'CartCouponsService',
    'CartMembershipsService',
    'CartEventsService',
    'ThemesHelperService',
    'UserPreferencesService',
    'CalendarService',
  ];
  private stateSubject = new BehaviorSubject<CartState>(createInitialState());
  state$ = combineLatest({
    status: this.stateSubject.pipe(map((state) => state.status)),
    autorenewInCart: this.stateSubject.pipe(map((state) => state.autorenewInCart)),
    memberships: this.memberships.state$,
  });
  model = {
    items: [],
    id: null,
    couponCodes: null,
    membershipIds: null,
    membershipCategoryId: null,
    verifyEntitlements: true,
    forms: [],
    giftCardRedemptions: [],
  } as {
    items: any[];
    id: any | null;
    couponCodes: any | null;
    membershipIds: any | null;
    membershipCategoryId: any | null;
    verifyEntitlements: boolean;
    orderContact?: OrderContact | null;
    reservationId?: any;
    forms: CartFormItem[];
    giftCardRedemptions?: GiftCardRedemption[];
  };
  itemCount = 0;
  initted = false;
  orderContact: Partial<OrderContact> | null = {};
  organization: Partial<Organization> | null = {};
  transactingMember: Partial<{
    member: any;
    membership: any;
    memberships: any;
    membershipCategoryId: any;
  }> | null = null;

  // Resolved order forms (set by getAllForms, cleared on cache invalidation)
  forms: CartOrderForm[] = [];

  // Forms cache
  private formsCache: {
    promise: IPromise<CartOrderForm[]> | null;
    timestamp: number | null;
  } = {
    promise: null,
    timestamp: null,
  };

  CODES = {
    errors: {
      SHOPPING_CART_DNE: 1033, // Shopping cart was not found
    },
  };

  constructor(
    private $q: IQService,
    private apiService: ApiService,
    private helpersService: TODO,
    public coupons: CartCouponsService,
    public memberships: CartMembershipsService,
    public events: CartEventsService,
    private themesHelperService: ThemesHelperService,
    private userPreferencesService: UserPreferencesService,
    private calendarService: CalendarService,
  ) {}

  init() {
    if (!this.initted) {
      this.memberships = this.memberships.init(this);
      this.coupons = this.coupons.init(this);
      this.events = this.events.init(this);
      this.getCart();
    }
    return this;
  }

  getSummary(options = {}) {
    return {
      data: this.memberships.getModel().concat(this.events.getEvents()),
      options: angular.extend(
        {
          editable: true,
          removable: true,
        },
        options,
      ),
    };
  }

  /**
   * Replaces all resolved order forms on the service.
   * Called internally by getAllForms after fetching.
   */
  setForms(forms: CartOrderForm[]): void {
    this.forms = forms;
  }

  /**
   * Updates the formResponseId on the matching CartFormItem in model.forms.
   * Matched by formId. No-ops if no matching item is found.
   */
  setFormResponseId(props: { formId: string; formResponseId: string }): void {
    const formItem = this.model.forms.find((f) => f.formId === props.formId);
    if (formItem) {
      formItem.formResponseId = props.formResponseId;
    }
  }

  /**
   * Gets all order forms from events (sync) and memberships (async) and merges them.
   * Sets this.forms with the resolved result, wiping any previously stored forms.
   * Uses caching to prevent duplicate API calls during multiple refresh() calls.
   * Cache is invalidated when cart items change.
   * @returns Promise resolving to array of CartOrderForm (events + memberships)
   */
  getAllForms(): IPromise<CartOrderForm[]> {
    // Return cached promise if it exists and is still pending
    if (this.formsCache.promise) {
      return this.formsCache.promise;
    }

    const eventForms = this.events.getEventForms();

    // Create and cache the promise
    this.formsCache.promise = this.memberships.getForms().then((membershipForms) => {
      this.formsCache.timestamp = Date.now();
      const allForms = [...eventForms, ...membershipForms];

      // Filter forms by sales channel and memberships before setting
      const memberships = this.memberships.model as Membership[];
      const filteredForms = deduplicateFormsByDisplaySettings([
        ...filterEventFormsBySalesChannel(
          allForms.filter((f): f is CartEventOrderForm => f.itemType === 'event'),
          'insideSales',
        ),
        ...filterMembershipFormsBySalesChannel(
          allForms.filter((f): f is CartMembershipOrderForm => isMembershipOrderForm(f)),
          memberships,
          'insideSalesIndividual',
        ),
      ]);

      this.setForms(filteredForms);

      // Check for missing forms in model.forms
      const missingForms = filteredForms.filter((form) => !(this.model.forms ?? []).some((f) => f.formId === form.id));
      if (missingForms.length) {
        const newItems: CartFormItem[] = missingForms.map(cartOrderFormToCartFormItem);
        this.model.forms = [...(this.model.forms ?? []), ...newItems];

        // Update the cart to add the new forms
        this.updateCart();
      }

      return filteredForms;
    });

    return this.formsCache.promise;
  }

  /**
   * Returns the IDs of all event forms associated with the given event ID.
   */
  getEventFormIds(eventId: string): IPromise<string[]> {
    return this.getAllForms().then((forms) =>
      forms.flatMap((f) => (f.itemType === 'event' && (f as CartEventOrderForm).event?.id === eventId ? [f.id] : [])),
    );
  }

  /**
   * Returns the IDs of all membership forms associated with the given membership category ID.
   */
  getMembershipFormIds(membershipCategoryId: string): IPromise<string[]> {
    return this.getAllForms().then((forms) =>
      forms.flatMap((f) =>
        isMembershipOrderForm(f) &&
        f.membershipLevelsInfo?.some((info) => info.membershipLevel.id === membershipCategoryId)
          ? [f.id]
          : [],
      ),
    );
  }

  /**
   * Removes entries from model.forms whose formId is in the provided list.
   */
  removeModelFormsByIds(formIds: string[]): void {
    this.model.forms = (this.model.forms ?? []).filter((f) => !formIds.includes(f.formId));
    this.forms = this.forms.filter((f) => !formIds.includes(f.id));
    this.invalidateFormsCache();
  }

  /**
   * Invalidates the forms cache and clears stored forms.
   * Should be called when cart items are added, removed, or modified.
   */
  private invalidateFormsCache(): void {
    this.formsCache.promise = null;
    this.formsCache.timestamp = null;
    this.forms = [];
  }

  /*
   * TODO: When we implement event tickets for organisations using the cart,
   * we need to enhance this to also check for organisation order contact
   */
  getOrderContact() {
    return this.model.orderContact?.customerId ? this.model.orderContact : null;
  }

  getOrderContactFullName() {
    const orderContact = this.model.orderContact;

    if (!orderContact) {
      return '';
    }

    return [orderContact.prefix, orderContact.firstName, orderContact.middleName, orderContact.lastName]
      .filter((name) => !!name)
      .join(' ');
  }

  getOrderContactType() {
    return 'Member';
  }

  /**
   * Prepares a valid payload to update the cart from our internal model
   */
  getSaveModel() {
    let model = angular.copy(this.model);
    const membershipIds = this.getTransactingMembershipId();

    angular.forEach(model.items, (v, k) => {
      v.couponCodes = null;
      delete v.couponCodes;
    });

    this.setTransactingMembershipCategory();
    model.membershipCategoryId = this.getTransactingMembershipCategory();
    model.membershipIds = membershipIds ? [membershipIds] : [];

    model = this.clearCouponsWhenAllItemsAreRemoved(model);

    // giftCardRedemptions is a server-owned, read-only field (redemptions are
    // managed via the storedvalue endpoint). Never echo it back on cart save —
    // the ShoppingCart VO expects a different shape and rejects the payload.
    delete model.giftCardRedemptions;

    return model;
  }

  /**
   * In B2B checkout when removing all items from a cart that had previously had a coupon applied,
   * it’s still living there in the background such that when you add another item to the cart,
   * the discounts are automatically applied but there is nowhere in the UI that shows the coupon.
   *
   * @link https://acmeticketing.atlassian.net/browse/ENG-13550
   *
   * @return {object} cart model
   */
  clearCouponsWhenAllItemsAreRemoved(cartModel) {
    if (cartModel.items.length === 0) {
      cartModel.couponCodes = [];
    }

    return cartModel;
  }

  getModel() {
    return this.model;
  }

  setModel(cart) {
    const reservationId = this.model.reservationId;

    this.model = cart;

    // Invalidate forms cache when cart model changes
    this.invalidateFormsCache();

    if (this.model) {
      if (this.model.id) {
        if (!reservationId) {
          //the first time we initialize our events, we also create a reservation id for the cart
          this.events
            .getReservation()
            .then((response) => {
              this.events.setReservation(response.payload);
            })
            .catch(() => {
              this.events.setReservation();
            });
        } else {
          this.setReservation(reservationId);
        }
      }

      if (this.model.couponCodes instanceof Array) {
        this.coupons.setModel(this.model.couponCodes);
      } else {
        this.coupons.resetModel();
      }
      if (this.model.items instanceof Array) {
        const memberships = this.model.items.filter((item) => item.itemType.toLowerCase().indexOf('membership') > -1);
        this.memberships.setModel(memberships);

        const events = this.model.items.filter((item) => {
          const itemType = item.itemType.toLowerCase();
          return itemType.indexOf('event') > -1 || itemType.indexOf('inventory') > -1;
        });
        this.events.setModel(events);

        this.itemCount = this.model.items.reduce((count, item) => {
          let quantity = parseInt(item.quantity, 10);
          if (isNaN(quantity)) {
            quantity = 0;
          }
          return quantity + count;
        }, 0);
      }
    }
  }

  refreshModel(data) {
    this.setModel(data);
    this.orderContact = this.getOrderContact();
    this.setTransactingMember(null);
    this.setTransactingMembershipCategory();
  }

  resetModel() {
    this.model = {
      items: [],
      id: null,
      couponCodes: null,
      membershipIds: null,
      membershipCategoryId: null,
      reservationId: null,
      verifyEntitlements: true,
      forms: [],
      giftCardRedemptions: [],
    };

    if (this.initted) {
      this.setModel(this.model);
      this.coupons.resetModel();
      this.memberships.resetModel();
      this.events.resetModel();
      this.orderContact = this.getOrderContact();
      this.updateOrganization(null);
      this.resetTransactingMember();
      this.memberships.clearCustomerData();
    }
  }

  processMembership(membership) {
    if (membership.membershipExpirationDate) {
      membership.membershipExpirationDateDisplay = this.calendarService.toFormat(
        this.calendarService.parseDate(membership.membershipExpirationDate),
        this.userPreferencesService.getDateFormat('shortDate', 'dateFormat'),
      );
    }

    return membership;
  }

  getMember(id: string) {
    return this.apiService.membership.cardholders.detail(id);
  }

  getMembership(id: number) {
    return this.apiService.membership.detail(id);
  }

  getMembershipSuccess(response) {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }

    this.transactingMember.membership = this.processMembership(response.data.payload);
  }

  getMembershipError(response) {
    if (this.transactingMember == null) {
      this.transactingMember = {};
    }

    this.transactingMember.memberships = [];
  }

  getTransactingMemberId() {
    let result = null;

    if (this.transactingMember && this.transactingMember.member) {
      result = this.transactingMember.member.id;
    }

    return result;
  }

  /**
   * This could return an array later when we support base + aux memberships.
   */
  getTransactingMembershipId() {
    const allowTicketTransactionOnExpiredMemberships = this.themesHelperService.getCompany(
      'allowTicketTransactionOnExpiredMemberships',
    );

    if (this.transactingMember && this.transactingMember.member) {
      const isExpiredMembership = this.memberships.isMembershipExpired(this.transactingMember.member);

      if (!isExpiredMembership || allowTicketTransactionOnExpiredMemberships) {
        return this.transactingMember.member.membershipId;
      }
    }

    return null;
  }

  hasTransactingMembershipCategory(categoryId) {
    const transactingMembershipCategory = this.getTransactingMembershipCategory();
    return transactingMembershipCategory && transactingMembershipCategory === categoryId;
  }

  getTransactingMembershipCategory() {
    return this.transactingMember ? this.transactingMember.membershipCategoryId : null;
  }

  setTransactingMembershipCategory() {
    const membershipLevelIds = this.memberships.getDiscountMembershipLevelIds(true),
      membershipLevelId = membershipLevelIds.length ? membershipLevelIds[0] : null;

    if (this.transactingMember) {
      this.transactingMember.membershipCategoryId = membershipLevelId;
    } else {
      //for new memberships there might not be an existing member.
      this.transactingMember = {
        membershipCategoryId: membershipLevelId,
      };
    }
  }

  getTransactingMembershipNumber() {
    let result = null;

    if (this.transactingMember && this.transactingMember.membership) {
      result = this.transactingMember.membership.acmeMembershipNumber;
    }

    return result;
  }

  getTransactingMembership() {
    return this.transactingMember && this.transactingMember.membership;
  }

  getTransactingMember() {
    return this.transactingMember && this.transactingMember.member;
  }

  setTransactingMember(member) {
    //get the cardholder and membership if it exists and
    // set the member and memberships that belong to this customer.
    const contact = this.getOrderContact();
    const cardId = contact ? contact.cardId : null;
    const updateTransactingMember = (member) => {
      if (!this.transactingMember) {
        this.transactingMember = {};
      }
      this.transactingMember.member = member;

      this.getMembership(member.membershipId)
        .then((response) => {
          this.getMembershipSuccess(response);
        })
        .catch((response) => {
          this.getMembershipError(response);
        });
    };

    if (cardId && this.getTransactingMemberId() !== cardId) {
      if (member) {
        updateTransactingMember(member);
      } else {
        this.getMember(cardId)
          .then((response) => {
            const member = response.data.payload;

            updateTransactingMember(member);
          })
          .catch(() => {
            //cardholder was not found. reset transacting member.
            this.resetTransactingMember();
          });
      }
    }

    if (!cardId) {
      this.resetTransactingMember();
    }
  }

  resetTransactingMember() {
    this.transactingMember = null;
    this.model.membershipIds = null;
  }

  setItems(items, resetCoupons) {
    this.model.items = items;
    if (resetCoupons) {
      this.coupons.resetModel();
    }
  }

  getItems() {
    return this.model.items || [];
  }

  getItem(itemId) {
    const items = this.getItems().filter((item) => {
      return item.itemId === itemId;
    });

    return items.length ? items[0] : null;
  }

  // TODO: Check if item has an id and try to update instead
  addItem(item: ShoppingCartItem) {
    this.model.items = ensureArray(this.model.items);
    this.model.items.push(item);

    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) {
        quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  updateItem(item) {
    const items = this.getItems();

    for (let i = 0, ii = items.length; i < ii; i++) {
      if (item.itemId === items[i].itemId) {
        items[i] = item;
        break;
      }
    }

    this.itemCount = this.model.items.reduce((count, item) => {
      let quantity = parseInt(item.quantity, 10);
      if (isNaN(quantity)) {
        quantity = 0;
      }
      return quantity + count;
    }, 0);
  }

  removeItem(itemId, skipGetCart = false) {
    let index = -1;
    const items = this.getItems();
    const q = this.$q.defer();

    for (let i = 0, ii = items.length; i < ii; i++) {
      if (itemId === items[i].itemId) {
        index = i;
        break;
      }
    }

    if (index > -1) {
      this.model.items.splice(index, 1);
    }

    this.memberships.setModel(
      this.model.items.filter((item) => item.itemType.toLowerCase().indexOf('membership') > -1),
    );

    this.events.setModel(
      this.model.items.filter((item) => {
        const itemType = item.itemType.toLowerCase();
        return itemType.indexOf('event') > -1 || itemType.indexOf('inventory') > -1;
      }),
    );

    if (!skipGetCart) {
      this.updateCart()
        .then((response) => {
          q.resolve(response);
        })
        .catch((response) => {
          q.reject(response);
        });
    } else {
      q.resolve();
      this.itemCount = this.model.items.reduce((count, item) => {
        let quantity = parseInt(item.quantity, 10);
        if (isNaN(quantity)) {
          quantity = 0;
        }
        return quantity + count;
      }, 0);
    }

    return q.promise;
  }

  clearMembershipDiscountedEvents() {
    const fromMembership = true;
    const promises = this.events.getEvents().map((event) => this.events.removeDiscountedTickets(event, fromMembership));

    return this.$q.all(promises);
  }

  removeItems(itemIds) {
    let index = -1;
    const remove = (itemId) => {
      const items = this.getItems();

      for (let i = 0, ii = items.length; i < ii; i++) {
        if (itemId === items[i].itemId) {
          index = i;
          break;
        }
      }

      if (index > -1) {
        this.model.items.splice(index, 1);
      }
    };

    angular.forEach(itemIds, (itemId) => {
      remove(itemId);
    });
  }

  getItemByType(itemType) {
    const items = this.getItems().filter((item) => {
      return item.itemType === itemType;
    });

    return items.length ? items[0] : null;
  }

  getMembershipItem() {
    const items = this.getItems().filter((item) => {
      return item.itemType.toLowerCase().indexOf('membership') > -1;
    });

    return items.length ? items[0] : null;
  }

  getEventItemById(eventItem) {
    const eventItemId = eventItem.itemId;
    const eventItemIsRetail = eventItem.isRetail || false;

    const items = this.getItems().filter((item) => {
      const isValidItemType =
          item.itemType.toLowerCase().indexOf('event') > -1 || item.itemType.toLowerCase().indexOf('inventory') > -1,
        isRetail = item.isRetail || false;
      return isValidItemType && item.itemId === eventItemId && isRetail === eventItemIsRetail;
    });

    return items.length ? items[0] : null;
  }

  getItemRetailSubtotal(item) {
    return integerToCurrencyFloat(item.quantity * currencyFloatToInteger(item.retailUnitPrice));
  }

  getItemDiscountedSubtotal(item) {
    return integerToCurrencyFloat(item.quantity * currencyFloatToInteger(item.unitPrice));
  }

  isOrderContact(id) {
    return id && this.model.orderContact && this.model.orderContact.customerId == id;
  }

  hasOrderContact() {
    return this.model.orderContact && this.model.orderContact.customerId;
  }

  setOrderContact(membership) {
    const membershipItem = membership || this.getMembershipItem();

    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId,
      );
    }
  }

  /*
   * TODO: When we implement event tickets for organisations using the cart,
   * we need to enhance this to also check for organisation order contact
   */
  updateOrderContact(membership?: any) {
    const membershipItem = membership || this.getMembershipItem();

    if (membershipItem) {
      this.model.orderContact = this.memberships.getOrderContact(
        membershipItem.membershipInfo.membershipCards || [],
        membershipItem.membershipInfo.orderContactId,
      );

      if (this.model.orderContact) {
        this.model.orderContact.cardId = this.model.orderContact.id;

        if (this.model.orderContact.membershipId) {
          this.setTransactingMember(this.model.orderContact);
        }
      } else {
        this.model.membershipIds = null;
      }
    } else {
      this.model.orderContact = null;
      this.model.membershipIds = null;
    }
  }

  // TODO: APP-4969 - update Organization type with the minimum required fields
  // TODO: APP-4969 - `CartOrganization` type with base fields needed?
  updateOrganization(organization: Partial<Organization> | null) {
    this.organization = organization;
  }

  getOrganization() {
    return this.organization;
  }

  setReservation(reservationId) {
    if (this.model) {
      this.model.reservationId = reservationId;
    }
  }

  getReservationId() {
    return this.model && this.model.reservationId ? this.model.reservationId : null;
  }

  isMemberCart() {
    return this.getTransactingMembershipId() || this.getTransactingMembershipCategory();
  }

  /**
   * Gets or creates a cart
   */
  getCart() {
    const q = this.$q.defer();

    this.apiService.cart
      .detail()
      .then((response) => {
        this.refreshModel(response.data.payload);
        this.initted = true;
        q.resolve(this.getModel());
      })
      .catch(() => {
        this.createCart()
          .then((response) => {
            // NOTE: do NOT call `resetModel()` here — `createCart()` has
            // already populated `this.model.id` with the new cart id, and
            // resetting wipes it back to null. Subsequent `updateCart()`
            // calls would then PUT to `/v2/api/carts/null` and the backend
            // would respond 400. (Bug introduced years ago, latent until
            // DEV-10599 changed the bootstrap order; see also the surrounding
            // discussion in the migration thread.)
            this.initted = true;
            q.resolve(response);
          })
          .catch((response) => {
            q.reject(response);
          });
      });

    return q.promise;
  }

  createCart(cart?: TODO) {
    const q = this.$q.defer();

    this.apiService.cart
      .create(cart || { verifyEntitlements: true })
      .then((response) => {
        this.model.id = response.data.payload;
        q.resolve(this.getModel());
      })
      .catch((response) => {
        q.reject(response);
      });

    return q.promise;
  }

  /**
   * Updates cart or creates a new cart if there is a failure
   */
  updateCart() {
    this.setStatus('loading');

    return this.apiService.cart
      .update(this.getSaveModel())
      .then((response) => {
        this.refreshModel(response.data.payload);
        return response;
      })
      .catch((response) => {
        if (response.status === 404) {
          return this.createCart(this.getSaveModel());
        }

        throw response;
      })
      .then((cartResponse) => {
        return this.runCouponValidationsAndReturnCartResponse(cartResponse);
      })
      .then((cartResponse) => {
        this.setStatus('idle');
        return cartResponse;
      });
  }

  runCouponValidationsAndReturnCartResponse(cartResponse) {
    const couponCodes = this.helpersService.path(cartResponse, ['data', 'payload', 'couponCodes'], []);
    const couponsModel = couponCodes.map((couponOrCode) => {
      return typeof couponOrCode === 'string' ? { code: couponOrCode } : couponOrCode.code;
    });

    this.coupons.setModel(couponsModel);

    return this.coupons
      .validate()
      .then(() => {
        return cartResponse;
      })
      .catch(() => {
        return cartResponse;
      });
  }

  /**
   * Clears all items in cart and
   * creates a new one for future use
   */
  cancelCart() {
    const q = this.$q.defer();
    const cart = this.getModel();

    if (cart && cart.id) {
      this.apiService.cart
        .delete(cart.id)
        .then(() => {
          this.invalidateFormsCache();
          this.resetModel();
          this.createCart().then((response) => {
            q.resolve(response);
          });
        })
        .catch((response) => {
          q.reject(response);
        });
    }

    return q.promise;
  }

  resetCart(skipReInit = false) {
    this.invalidateFormsCache();
    this.resetModel();
    this.initted = false;
    if (!skipReInit) {
      this.init();
    }
  }

  /**
   * Adds cart item
   */
  addCartItem(item: ShoppingCartItem) {
    const cart = this.getModel();

    if (cart?.id == null) {
      return this.$q.reject();
    }

    return this.apiService.cart.item.add(cart.id, item);
  }

  /**
   * Updates cart item
   */
  updateCartItem(item: ShoppingCartItem) {
    const cart = this.getModel();

    if (cart?.id == null) {
      return this.$q.reject();
    }

    // Invalidate forms cache when cart item is updated
    this.invalidateFormsCache();

    return this.apiService.cart.item.update(cart.id, item);
  }

  getTotal() {
    let total = 0;

    angular.forEach(this.memberships.getModel(), (membership) => {
      total += currencyFloatToInteger(membership.unitPrice, { significant: 2 });
    });

    angular.forEach(this.events.getEvents(), (event) => {
      total += this.getTicketAmount(event, false);
      total += this.getAddOnAmount(event, false);
    });

    return integerToCurrencyFloat(total, { significant: 2 });
  }

  getTicketAmount(event, retail = false) {
    let result = 0;
    if (event.tickets instanceof Array) {
      angular.forEach(event.tickets, (ticket) => {
        if (ticket.quantity) {
          if (retail) {
            result += ticket.quantity * currencyFloatToInteger(ticket.retailUnitPrice, { significant: 2 });
          } else {
            result += ticket.quantity * currencyFloatToInteger(ticket.unitPrice, { significant: 2 });
          }
        }
      });
    }

    return result;
  }

  getAddOnAmount(event, retail = false) {
    let result = 0;
    if (event.addOns instanceof Array) {
      angular.forEach(event.addOns, (addOn) => {
        if (addOn.quantity) {
          if (retail) {
            result += addOn.quantity * currencyFloatToInteger(addOn.unitPrice, { significant: 2 });
          } else {
            result += addOn.quantity * currencyFloatToInteger(addOn.unitPrice, { significant: 2 });
          }
        }
      });
    }

    return result;
  }

  getRetailTotal() {
    let retailTotal = 0;

    angular.forEach(this.memberships.getModel(), (membership) => {
      retailTotal += currencyFloatToInteger(membership.retailUnitPrice, { significant: 2 });
    });

    angular.forEach(this.events.getEvents(), (event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
    });

    return integerToCurrencyFloat(retailTotal, { significant: 2 });
  }

  getSavings() {
    let retailTotal = 0;
    let total = 0;

    angular.forEach(this.memberships.getModel(), (item) => {
      retailTotal += currencyFloatToInteger(item.retailUnitPrice, { significant: 2 });
      total += currencyFloatToInteger(item.unitPrice, { significant: 2 });
    });

    angular.forEach(this.events.getEvents(), (event) => {
      retailTotal += this.getTicketAmount(event, true);
      retailTotal += this.getAddOnAmount(event, true);
      total += this.getTicketAmount(event);
      total += this.getAddOnAmount(event);
    });

    const savings = retailTotal - total;

    return integerToCurrencyFloat(savings, { significant: 2 });
  }

  setVerifyEntitlements(ignoreLimits) {
    this.model.verifyEntitlements = ignoreLimits ? false : true;
  }

  send(
    event:
      | { type: 'RESET' }
      | { type: 'STATUS_CHANGED'; params: Pick<CartState, 'status'> }
      | { type: 'AUTORENEW_IN_CART_CHANGED'; params: Pick<CartState, 'autorenewInCart'> },
  ) {
    switch (event.type) {
      case 'RESET':
        this.memberships.send({ type: 'RESET' });
        this.stateSubject.next(createInitialState());
        break;

      case 'STATUS_CHANGED':
        this.stateSubject.next({
          ...this.stateSubject.value,
          status: event.params.status,
        });
        break;

      case 'AUTORENEW_IN_CART_CHANGED':
        this.stateSubject.next({
          ...this.stateSubject.value,
          autorenewInCart: createAutorenewInCart(event.params.autorenewInCart),
        });
        break;

      default:
        break;
    }
  }

  setStatus(status: CartStatus) {
    this.send({ type: 'STATUS_CHANGED', params: { status } });
  }

  waitFor(targetStatus: CartStatus) {
    return firstValueFrom(this.state$.pipe(first((state) => state.status === targetStatus)));
  }

  setAutorenewInCart(autorenewInCart: AutorenewInCart) {
    this.send({ type: 'AUTORENEW_IN_CART_CHANGED', params: { autorenewInCart } });
  }

  getSnapshot() {
    return this.stateSubject.value;
  }
}

export const CartServiceModule = angular
  .module('acme.common.service.cart', [
    'acme.common.service.api',
    CartCouponsServiceModule,
    CartMembershipsServiceModule,
    CartEventsServiceModule,
    'acme.common.service.userPreferences',
    'acme.common.service.calendar',
    'acme.common.service.themesHelper',
  ])
  .service('CartService', CartService).name;
